import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  IconButton,
  Pane,
  Paragraph,
} from "evergreen-ui";
import { Meta } from "../../../components/Meta/Meta";
import { useGFTheme } from "../../../lib/themes";
import { H1 } from "../../../components/Heading/Heading";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import tajc from "./images/ajc.svg";
import shoutout_atlanta from "./images/shoutout_atlanta.svg";
import anf from "./images/anf.svg";
import carousel1 from "./images/carousel1.png";
import carousel2 from "./images/carousel2.png";
import carousel3 from "./images/carousel3.png";
import carousel4 from "./images/carousel4.png";
import eventsForConnection from "./images/events_for_connection.png";
import warmWelcoming from "./images/warm_welcoming.png";
import activityBasedFun from "./images/activity_based_fun.png";
import designedForConnection from "./images/designed_for_connection.png";
import classNames from "classnames";
import { Swiper as SwiperType } from "swiper/types";
import { AccordionItem } from "../../../components/AccordionItem/AccordionItem";

import backgroundVideo from "./hero_small.mp4?url";
import styles from "./NewHome.module.css";
import "swiper/css";
import "swiper/css/navigation";
import { useEffect, useRef, useState } from "react";

const carouselImages = [
  { image: carousel1, caption: "Rock Climbing" },
  { image: carousel2, caption: "Happy Hour" },
  { image: carousel3, caption: "Bowling" },
  { image: carousel4, caption: "Board Games" },
];

const quotes = [
  {
    quote:
      "With Green Flag, I got at least one date out of every event I have attended (sometimes more!)",
    name: "Meagan, 37",
  },
  {
    quote:
      "It was so refreshing to be out with new people, playing games, and hearing stories.",
    name: "Brian, 43",
  },
  {
    quote:
      "I've had such a great time at the Green Flag events. It's a much better alternative to the dating apps. It's like going to a normal event but you already know everyone is single. It's absolutely worth a try!",
    name: "Bree 33",
  },
  {
    quote:
      "It was great meeting all sorts of women at this event. I'm even planning to do some fun activities with some of the guys I met there as well!",
    name: "Jordan, 31",
  },
];

export const NewHome = () => {
  const { colors } = useGFTheme();
  const navigate = useNavigate();
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Wait until the browser guarantees smooth playback
    const onReady = () => {
      setVideoReady(true);
      v.play(); // start only now
    };

    v.addEventListener("canplaythrough", onReady, { once: true });
    return () => v.removeEventListener("canplaythrough", onReady);
  }, []);

  const handleQuoteSwiperInit = (swiper: SwiperType) => {
    // Defer measurement slightly to ensure DOM is rendered
    setTimeout(() => {
      let maxHeight = 0;
      if (swiper.slides) {
        // Measure the tallest slide
        swiper.slides.forEach((slideEl: HTMLElement) => {
          const slideHeight = slideEl.offsetHeight;
          if (slideHeight > maxHeight) {
            maxHeight = slideHeight;
          }
        });

        // Set each slide to that height
        swiper.slides.forEach((slideEl: HTMLElement) => {
          slideEl.style.height = `${maxHeight}px`;
        });

        // Force Swiper to recalc its layout
        swiper.update();
      }
    }, 750);
  };
  return (
    <Pane>
      <Meta>
        <title>Green Flag - Dicover Connections Offline</title>
        <script src="https://cdn.lightwidget.com/widgets/lightwidget.js"></script>
      </Meta>

      <Pane
        borderTop={`1px solid ${colors.greenDark}`}
        borderBottom={`2px solid ${colors.greenDark}`}
        position="relative"
        width="100%"
        overflow="hidden"
        backgroundColor={colors.greenPrimary}
      >
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: videoReady ? 1 : 0,
            transition: "opacity .5s ease",
            objectFit: "cover",
          }}
        >
          <source src={backgroundVideo} type="video/mp4" />
        </video>
        <Pane
          display="flex"
          flexDirection="column"
          alignItems="center"
          paddingX="1rem"
          className={styles.hero}
        >
          <Pane
            textAlign="center"
            overflow="hidden"
            className={styles.h1Container}
          >
            <H1
              className={styles.h1}
              color={colors.greenLight}
              textAlign="center"
              display="inline"
              background={`linear-gradient(transparent 10%, ${colors.greenDark} 10% 90%, transparent 90%)`}
            >
              Discover Connections
              <br />
              Offline.
            </H1>
          </Pane>

          <Button
            className={styles.eventsButton}
            appearance="primary"
            size="small"
            onClick={() => navigate("/events")}
          >
            Explore Upcoming Events
          </Button>
        </Pane>
      </Pane>
      <Pane
        display="flex"
        flexDirection="column"
        alignItems="center"
        borderBottom={`2px solid ${colors.greenDark}`}
      >
        <Pane marginTop="1.2rem">AS SEEN IN</Pane>
        <Pane
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginX="1rem"
          marginY="1.2rem"
          paddingX="0.5rem"
          gap="1rem"
          width="100%"
          maxWidth="40rem"
          className={styles.logos}
        >
          <Pane
            is="a"
            flex={1}
            display="block"
            target="_blank"
            href="https://www.ajc.com/news/atlanta-news/how-far-to-go-for-love-for-some-atlanta-traffic-is-a-dating-deal-breaker/XX7SYAKYXJGLBMM6MGF5LJK3LI/"
          >
            <Pane is="img" src={tajc} width="100%" />
          </Pane>
          <Pane
            is="a"
            flex={1}
            display="block"
            target="_blank"
            href="https://shoutoutatlanta.com/meet-abby-larner-co-founder-and-ceo-of-green-flag-date/"
          >
            <Pane is="img" src={shoutout_atlanta} width="100%" />
          </Pane>
          <Pane
            is="a"
            flex={1}
            display="block"
            target="_blank"
            href="https://www.atlantanewsfirst.com/2025/04/10/green-flag-dating-creating-lasting-connections/"
          >
            <Pane is="img" src={anf} width="100%" />
          </Pane>
        </Pane>
      </Pane>
      <Pane>
        <Pane
          is="h2"
          fontFamily="league-gothic"
          color={colors.greenDark}
          textAlign="center"
          paddingX="1rem"
          className={classNames(styles.h2a, styles.h2)}
        >
          Meet People Doing What You Love
        </Pane>
        <Paragraph
          size={500}
          maxWidth="41rem"
          marginX="auto"
          textAlign="center"
          marginTop="2rem"
          paddingX="1rem"
          color={colors.greenDark}
        >
          Green Flag offers events for daters where you can regularly meet in
          person first, connect authentically, and follow up on our platform if
          the chemistry clicks.
        </Paragraph>
      </Pane>
      <Pane
        position="relative"
        maxWidth="70rem"
        marginX="auto"
        className={styles.imgContainer}
      >
        <IconButton
          appearance="minimal"
          icon={ChevronLeftIcon}
          className="imgPrev"
          iconSize={40}
          position="absolute"
          left="1rem"
          top="calc(50% - 20px)"
          selectors={{
            "&:hover svg": {
              fill: colors.grayPrimary,
            },
          }}
        />
        <IconButton
          appearance="minimal"
          icon={ChevronRightIcon}
          className="imgNext"
          iconSize={40}
          position="absolute"
          right="1rem"
          top="calc(50% - 20px)"
          selectors={{
            "&:hover svg": {
              fill: colors.grayPrimary,
            },
          }}
        />
        <Swiper
          spaceBetween={16}
          slidesPerView={1}
          modules={[Autoplay, Navigation]}
          autoplay={{
            delay: 10000,
            disableOnInteraction: true,
          }}
          navigation={{
            prevEl: ".imgPrev",
            nextEl: ".imgNext",
          }}
          loop={true}
          style={{ margin: "0 4rem" }}
          breakpoints={{
            575: {
              spaceBetween: 20,
              slidesPerView: 3,
            },
          }}
          wrapperClass={styles.slideWrapper}
        >
          {carouselImages.map((val, index) => (
            <SwiperSlide key={index}>
              <Pane
                is="img"
                src={val.image}
                width="100%"
                height="100%"
                borderRadius="0.5rem"
                overflow="hidden"
              />
              <Pane
                fontFamily="league-gothic"
                fontSize="1.25rem"
                textTransform="uppercase"
                color={colors.greenDark}
                textAlign="center"
              >
                {val.caption}
              </Pane>
            </SwiperSlide>
          ))}
        </Swiper>
      </Pane>
      <Pane maxWidth="100%" overflow="hidden">
        <Pane
          borderTopLeftRadius="50%"
          borderTopRightRadius="50%"
          backgroundColor="#39B87D"
          minHeight="23vw"
          marginLeft="-80%"
          marginRight="-80%"
          marginTop="5rem"
          paddingX="80%"
          className={styles.semicircleBackground}
        >
          <Pane
            paddingX="2rem"
            flexDirection="column"
            alignItems="center"
            maxWidth="50rem"
            marginX="auto"
            className={styles.eventsForConnectionSmall}
          >
            <Pane
              is="h2"
              fontFamily="league-gothic"
              color={colors.greenDark}
              textAlign="center"
              className={classNames(styles.h2b, styles.h2)}
              flex={1}
            >
              Events Designed for Authentic, Effortless Connection
            </Pane>
            <Pane
              flex={1}
              paddingX="2rem"
              className={styles.eventsForConnection}
              maxWidth="31rem"
            >
              <Pane
                is="img"
                src={eventsForConnection}
                display="block"
                width="100%"
                borderRadius="0.5rem"
                overflow="hidden"
              />
            </Pane>
            <Paragraph size={500} color={colors.greenDark}>
              Our weekly events bring together people in a relaxed, low-pressure
              setting where it's easy to be yourself and actually enjoy meeting
              new people. Instead of forced small talk, you'll connect over fun,
              shared experiences—like trivia, bowling, board games, and more.
            </Paragraph>
          </Pane>
          <Pane
            paddingX="2rem"
            alignItems="center"
            maxWidth="50rem"
            marginX="auto"
            gap="4rem"
            className={styles.eventsForConnectionLarge}
          >
            <Pane
              is="img"
              src={eventsForConnection}
              display="block"
              width="100%"
              borderRadius="1.25rem"
              overflow="hidden"
              flex={1}
            />
            <Pane flex={1}>
              <Pane
                is="h2"
                fontFamily="league-gothic"
                color={colors.greenDark}
                fontSize="3rem"
                marginBottom="1rem"
                flex={1}
              >
                Events Designed for Authentic, Effortless Connection
              </Pane>
              <Paragraph size={500} color={colors.greenDark}>
                Our weekly events bring together people in a relaxed,
                low-pressure setting where it's easy to be yourself and actually
                enjoy meeting new people. Instead of forced small talk, you'll
                connect over fun, shared experiences—like trivia, bowling, board
                games, and more.
              </Paragraph>
            </Pane>
          </Pane>
        </Pane>
      </Pane>

      <Pane
        is="h2"
        fontFamily="league-gothic"
        color={colors.greenDark}
        fontSize="3rem"
        marginTop="5rem"
        marginBottom="1.5rem"
        flex={1}
        textAlign="center"
        className={styles.h2}
      >
        Loved by Real People Like You
      </Pane>
      <Pane position="relative" maxWidth="70rem" marginX="auto">
        <IconButton
          appearance="minimal"
          icon={ChevronLeftIcon}
          className={styles.quotePrev}
          iconSize={40}
          position="absolute"
          left="1rem"
          top="calc(50% - 20px)"
          selectors={{
            "&:hover svg": {
              fill: colors.grayPrimary,
            },
          }}
        />
        <IconButton
          appearance="minimal"
          icon={ChevronRightIcon}
          className={styles.quoteNext}
          iconSize={40}
          position="absolute"
          right="1rem"
          top="calc(50% - 20px)"
          selectors={{
            "&:hover svg": {
              fill: colors.grayPrimary,
            },
          }}
        />
        <Swiper
          spaceBetween={16}
          slidesPerView={1}
          onInit={handleQuoteSwiperInit}
          modules={[Autoplay, Navigation]}
          navigation={{
            prevEl: `.${styles.quotePrev}`,
            nextEl: `.${styles.quoteNext}`,
          }}
          autoplay={{
            delay: 10000,
            disableOnInteraction: true,
          }}
          loop={true}
          style={{
            maxWidth: "70rem",
            marginTop: "5rem",
            margin: "0 4rem",
          }}
          className={styles.quoteSwiper}
          breakpoints={{
            600: {
              slidesPerView: 2,
            },
            990: {
              slidesPerView: 3,
            },
            1024: {
              spaceBetween: 40,
              slidesPerView: 4,
            },
          }}
          wrapperClass={styles.quoteWrapper}
        >
          {quotes.map((val, index) => (
            <SwiperSlide key={index}>
              <Pane
                borderRadius="1.25rem"
                backgroundColor="white"
                padding="1.5rem"
                border="2px solid #1A3224"
                height="100%"
                display="flex"
                flexDirection="column"
              >
                <Pane
                  fontSize="8rem"
                  fontWeight="bold"
                  height="5rem"
                  marginBottom="1rem"
                  marginTop="-1.4rem"
                  color={colors.greenLight}
                >
                  “
                </Pane>
                <Pane
                  fontStyle="italic"
                  color="#1A3224"
                  lineHeight="160%"
                  fontSize="1rem"
                  flex={1}
                >
                  “{val.quote}“
                </Pane>
                <Pane fontWeight="bold" marginTop="0.5rem">
                  {val.name}
                </Pane>
              </Pane>
            </SwiperSlide>
          ))}
        </Swiper>
      </Pane>
      <Pane
        is="h2"
        fontFamily="league-gothic"
        color={colors.greenDark}
        textAlign="center"
        paddingX="1rem"
        className={classNames(styles.h2)}
        marginTop="5rem"
        marginBottom="1.5rem"
      >
        What It's Like to Come to a Green Flag Event
      </Pane>
      <Pane display="flex" padding="2rem" className={styles.whatItsLike}>
        <Pane
          borderRadius="0.625rem"
          backgroundColor={colors.greenDark}
          maxWidth="20rem"
          display="flex"
          flexDirection="column"
        >
          <Pane
            is="h3"
            fontFamily="league-gothic"
            textTransform="uppercase"
            color={colors.greenLight}
            textAlign="center"
            paddingX="2rem"
            paddingTop="2rem"
            fontSize="2rem"
            marginBottom="2rem"
          >
            WARM AND WELCOMING
          </Pane>
          <Paragraph
            size={500}
            color="white"
            textAlign="center"
            paddingX="2rem"
            marginBottom="2rem"
            flex={1}
          >
            Low-pressure, inclusive events perfect for anyone, even if you're
            coming solo.
          </Paragraph>

          <Pane
            borderTopLeftRadius="50% 75%"
            borderTopRightRadius="50% 75%"
            width="100%"
            overflow="hidden"
            backgroundImage={`url(${warmWelcoming})`}
            backgroundSize="100% auto"
            backgroundPosition="top"
            style={{
              aspectRatio: "7 / 4",
            }}
          />
        </Pane>
        <Pane
          borderRadius="0.625rem"
          backgroundColor={colors.greenDark}
          maxWidth="20rem"
          display="flex"
          flexDirection="column"
        >
          <Pane
            is="h3"
            fontFamily="league-gothic"
            textTransform="uppercase"
            color={colors.greenLight}
            textAlign="center"
            paddingX="2rem"
            paddingTop="2rem"
            fontSize="2rem"
            marginBottom="2rem"
          >
            ACTIVITY-BASED FUN
          </Pane>
          <Paragraph
            size={500}
            color="white"
            textAlign="center"
            paddingX="2rem"
            marginBottom="2rem"
            flex={1}
          >
            Icebreakers and activities keep things engaging and organized
            allowing you to have fun connecting.
          </Paragraph>

          <Pane
            borderTopLeftRadius="50% 75%"
            borderTopRightRadius="50% 75%"
            width="100%"
            overflow="hidden"
            backgroundImage={`url(${activityBasedFun})`}
            backgroundSize="100% auto"
            backgroundPosition="top"
            style={{
              aspectRatio: "7 / 4",
            }}
          />
        </Pane>
        <Pane
          borderRadius="0.625rem"
          backgroundColor={colors.greenDark}
          maxWidth="20rem"
          display="flex"
          flexDirection="column"
        >
          <Pane
            is="h3"
            fontFamily="league-gothic"
            textTransform="uppercase"
            color={colors.greenLight}
            textAlign="center"
            paddingX="2rem"
            paddingTop="2rem"
            fontSize="2rem"
            marginBottom="2rem"
          >
            DESIGNED FOR CONNECTION
          </Pane>
          <Paragraph
            size={500}
            color="white"
            textAlign="center"
            paddingX="2rem"
            marginBottom="2rem"
            flex={1}
          >
            Inviting settings, 40 - 60 person groups, and shared activities that
            spark chemistry and allow for deeper connections
          </Paragraph>

          <Pane
            borderTopLeftRadius="50% 75%"
            borderTopRightRadius="50% 75%"
            width="100%"
            overflow="hidden"
            backgroundImage={`url(${designedForConnection})`}
            backgroundSize="100% auto"
            backgroundPosition="top"
            style={{
              aspectRatio: "7 / 4",
            }}
          />
        </Pane>
      </Pane>
      <Pane
        is="h2"
        fontFamily="league-gothic"
        color={colors.greenDark}
        textAlign="left"
        paddingX="2rem"
        className={classNames(styles.h2)}
        marginTop="5rem"
        marginBottom="1.5rem"
      >
        FAQ
      </Pane>
      <Pane
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        paddingX="2rem"
      >
        <AccordionItem
          title="Do you provide ways to stay in touch with people I meet?"
          borderTop={`1px solid ${colors.greenDark}`}
          paddingY="1rem"
        >
          <Paragraph
            size={500}
            marginTop="1rem"
            maxWidth="45rem"
            color={colors.greenDark}
          >
            Yes! After each event, you'll get access to our matching platform
            and be able to see everyone who attended. You can invite others to
            connect, and if the interest is mutual, we'll share whatever contact
            information you choose—no pressure, no awkward guessing.
          </Paragraph>
        </AccordionItem>
        <AccordionItem
          title="What is the cancellation or refund policy?"
          borderTop={`1px solid ${colors.greenDark}`}
          paddingY="1rem"
        >
          <Paragraph
            size={500}
            marginTop="1rem"
            maxWidth="45rem"
            color={colors.greenDark}
          >
            We understand that plans can change! If you need to cancel your
            reservation, you can receive a full refund as long as you cancel at
            least 48 hours before the event. Unfortunately, we're unable to
            offer refunds for cancellations made within 48 hours of the event.
            This helps us maintain a great balance of attendees and cover venue
            costs. Thanks for your understanding! If we need to cancel an event
            for any reason, you will receive a full refund.
          </Paragraph>
        </AccordionItem>
        <AccordionItem
          title="Are Green Flag events inclusive and LGBTQ+ friendly?"
          borderTop={`1px solid ${colors.greenDark}`}
          paddingY="1rem"
        >
          <Paragraph size={500} marginTop="1rem" color={colors.greenDark}>
            Our events are designed to be inclusive and welcoming to adults 21+,
            backgrounds, and interests. Specific events may cater to certain age
            groups or demographics, but we aim to create spaces where everyone
            feels comfortable.
          </Paragraph>
        </AccordionItem>
        <AccordionItem
          title="What is your Code of Conduct?"
          borderTop={`1px solid ${colors.greenDark}`}
          paddingY="1rem"
        >
          <Pane marginTop="1rem" maxWidth="45rem">
            <Paragraph size={500} fontWeight="bold" color={colors.greenDark}>
              Respect and Consideration
            </Paragraph>
            <Paragraph size={500} marginBottom="1rem" color={colors.greenDark}>
              Treat everyone you meet with kindness and courtesy. Respect
              personal boundaries, and remember that everyone is here to have a
              positive experience.
            </Paragraph>

            <Paragraph size={500} fontWeight="bold" color={colors.greenDark}>
              No Ghosting Policy
            </Paragraph>
            <Paragraph size={500} marginBottom="1rem" color={colors.greenDark}>
              If you connect with someone at an event, communicate openly and
              considerately after the social. Ghosting is a no-go—we're all
              about fostering genuine and respectful connections.
            </Paragraph>

            <Paragraph size={500} fontWeight="bold" color={colors.greenDark}>
              Inclusivity Over Exclusivity
            </Paragraph>
            <Paragraph size={500} marginBottom="1rem" color={colors.greenDark}>
              If you're in a group, make an effort to include anyone who came
              solo. No cliques, no competitive energy—just great conversations!
            </Paragraph>

            <Paragraph size={500} fontWeight="bold" color={colors.greenDark}>
              Zero Tolerance for Inappropriate Behavior
            </Paragraph>
            <Paragraph size={500} marginBottom="1rem" color={colors.greenDark}>
              Respectful conduct only: Any form of harassment, unwelcome
              behavior, or disrespect will not be tolerated. Attendees engaging
              in such actions will be immediately removed from the event and
              permanently banned from future events.
            </Paragraph>
          </Pane>
        </AccordionItem>
      </Pane>
    </Pane>
  );
};
